import React, { useContext, useEffect, useRef, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import API from "../../api/api";
import { resolveAssetUrl } from "../../api/config";
import Navbar from "../../components/Navbar";
import { AuthContext } from "../../context/AuthContext";
import { confirmAlert, toastMessageAlert } from "../../utils/alert";

const CROPPED_WIDTH = 1600;
const CROPPED_HEIGHT = 900;
const PREVIEW_WIDTH = 640;
const PREVIEW_HEIGHT = 360;
const MAX_SLIDER_BATCH_UPLOAD = 20;
const FIT_MODE_COVER = "cover";
const FIT_MODE_CONTAIN = "contain";
const FIT_MODE_AUTO = "auto";
const CROP_CONTEXT_ADD = "add";
const CROP_CONTEXT_EDIT = "edit";

const clamp01 = (value) => Math.min(1, Math.max(0, value));
const normalizeFitMode = (value) => {
  const mode = String(value || "").toLowerCase();
  if (mode === FIT_MODE_CONTAIN) return FIT_MODE_CONTAIN;
  if (mode === FIT_MODE_AUTO) return FIT_MODE_AUTO;
  return FIT_MODE_COVER;
};
const fitModeLabel = (mode) => {
  const normalized = normalizeFitMode(mode);
  if (normalized === FIT_MODE_CONTAIN) return "Fit to Page";
  if (normalized === FIT_MODE_AUTO) return "Auto";
  return "Crop Fill";
};
const sliderPreviewFrameClass = (mode) =>
  normalizeFitMode(mode) === FIT_MODE_COVER ? "bg-slate-200" : "bg-slate-900";
const sliderPreviewImageClass = (mode) => {
  const normalized = normalizeFitMode(mode);
  if (normalized === FIT_MODE_COVER) return "object-contain";
  if (normalized === FIT_MODE_CONTAIN) return "object-contain";
  if (normalized === FIT_MODE_AUTO) return "object-contain";
  return "object-contain";
};
const sliderThumbImageClass = (mode) =>
  normalizeFitMode(mode) === FIT_MODE_COVER ? "object-contain" : "object-contain";

const drawCropToCanvas = (
  image,
  canvas,
  {
    focusX,
    focusY,
    zoom,
    trimLeft = 0,
    trimRight = 0,
    trimTop = 0,
    trimBottom = 0,
  }
) => {
  const ctx = canvas.getContext("2d");
  if (!ctx || !image) return;

  const targetRatio = canvas.width / canvas.height;
  const imageWidth = image.naturalWidth || 1;
  const imageHeight = image.naturalHeight || 1;

  let leftPct = Math.max(0, Math.min(45, Number(trimLeft) || 0));
  let rightPct = Math.max(0, Math.min(45, Number(trimRight) || 0));
  let topPct = Math.max(0, Math.min(45, Number(trimTop) || 0));
  let bottomPct = Math.max(0, Math.min(45, Number(trimBottom) || 0));

  if (leftPct + rightPct > 90) {
    const scale = 90 / (leftPct + rightPct);
    leftPct *= scale;
    rightPct *= scale;
  }
  if (topPct + bottomPct > 90) {
    const scale = 90 / (topPct + bottomPct);
    topPct *= scale;
    bottomPct *= scale;
  }

  const trimmedLeft = imageWidth * (leftPct / 100);
  const trimmedRight = imageWidth * (rightPct / 100);
  const trimmedTop = imageHeight * (topPct / 100);
  const trimmedBottom = imageHeight * (bottomPct / 100);
  const availableW = Math.max(1, imageWidth - trimmedLeft - trimmedRight);
  const availableH = Math.max(1, imageHeight - trimmedTop - trimmedBottom);
  const imageRatio = availableW / availableH;

  let baseCropWidth;
  let baseCropHeight;

  if (imageRatio > targetRatio) {
    baseCropHeight = availableH;
    baseCropWidth = baseCropHeight * targetRatio;
  } else {
    baseCropWidth = availableW;
    baseCropHeight = baseCropWidth / targetRatio;
  }

  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const safeZoom = Math.max(0.5, Number(zoom) || 1);
  if (safeZoom <= 1) {
    // Fill side padding with the same image at low opacity for a softer letterbox.
    ctx.save();
    ctx.globalAlpha = 0.12;
    ctx.filter = "blur(4px)";
    ctx.drawImage(image, trimmedLeft, trimmedTop, availableW, availableH, 0, 0, canvas.width, canvas.height);
    ctx.restore();

    const containRatio = imageRatio;
    let containW;
    let containH;
    if (containRatio > targetRatio) {
      containW = canvas.width;
      containH = containW / containRatio;
    } else {
      containH = canvas.height;
      containW = containH * containRatio;
    }

    const drawW = containW * safeZoom;
    const drawH = containH * safeZoom;
    const maxOffsetX = Math.max(0, canvas.width - drawW);
    const maxOffsetY = Math.max(0, canvas.height - drawH);
    const destX = maxOffsetX * clamp01(focusX);
    const destY = maxOffsetY * clamp01(focusY);

    ctx.drawImage(
      image,
      trimmedLeft,
      trimmedTop,
      availableW,
      availableH,
      destX,
      destY,
      drawW,
      drawH
    );
    return;
  }

  const cropWidth = baseCropWidth / safeZoom;
  const cropHeight = baseCropHeight / safeZoom;
  const maxX = Math.max(0, availableW - cropWidth);
  const maxY = Math.max(0, availableH - cropHeight);
  const sourceX = trimmedLeft + maxX * clamp01(focusX);
  const sourceY = trimmedTop + maxY * clamp01(focusY);

  ctx.drawImage(
    image,
    sourceX,
    sourceY,
    cropWidth,
    cropHeight,
    0,
    0,
    canvas.width,
    canvas.height
  );
};

const ManageSliders = () => {
  const { role } = useContext(AuthContext);
  const [sliders, setSliders] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    images: [],
    fitMode: FIT_MODE_COVER,
  });
  const [message, setMessage] = useState("");
  const [editingSlider, setEditingSlider] = useState(null);
  const [editForm, setEditForm] = useState({
    title: "",
    description: "",
    fitMode: FIT_MODE_COVER,
  });
  const [editImageFile, setEditImageFile] = useState(null);
  const [editImagePreviewUrl, setEditImagePreviewUrl] = useState("");
  const [isPreparingEditImage, setIsPreparingEditImage] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const [cropQueue, setCropQueue] = useState([]);
  const [cropIndex, setCropIndex] = useState(-1);
  const [cropContext, setCropContext] = useState(CROP_CONTEXT_ADD);
  const [cropSourceUrl, setCropSourceUrl] = useState("");
  const [cropImage, setCropImage] = useState(null);
  const [cropX, setCropX] = useState(50);
  const [cropY, setCropY] = useState(50);
  const [cropZoom, setCropZoom] = useState(1);
  const [cropTrimLeft, setCropTrimLeft] = useState(0);
  const [cropTrimRight, setCropTrimRight] = useState(0);
  const [cropTrimTop, setCropTrimTop] = useState(0);
  const [cropTrimBottom, setCropTrimBottom] = useState(0);
  const [cropError, setCropError] = useState("");
  const [isApplyingCrop, setIsApplyingCrop] = useState(false);
  const [isPreviewDragging, setIsPreviewDragging] = useState(false);
  const [isCropFrameDragging, setIsCropFrameDragging] = useState(false);
  const [cropFrameDrag, setCropFrameDrag] = useState(null);
  const [preparedPreviews, setPreparedPreviews] = useState([]);

  const previewCanvasRef = useRef(null);
  const cropFrameContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);

  const editCurrentImageUrl = editingSlider
    ? resolveAssetUrl(editingSlider.image, { folder: "sliders" })
    : "";
  const isCropOpen = cropIndex >= 0 && cropIndex < cropQueue.length;
  const currentCropFile = isCropOpen ? cropQueue[cropIndex] : null;

  const loadSliders = async () => {
    try {
      const res = await API.fetchSliders();
      const normalized = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
          ? res.data
          : [];
      setSliders(normalized);
      setMessage("");
    } catch (err) {
      console.error("Error fetching sliders:", err);
      setSliders([]);
      setMessage("Error: Failed to load sliders.");
    }
  };

  useEffect(() => {
    loadSliders();
  }, []);

  useEffect(() => {
    if (!message) return;
    toastMessageAlert(message, { timer: 2500 });
  }, [message]);

  useEffect(() => {
    const urls = (formData.images || []).map((file) => ({
      name: file.name,
      url: URL.createObjectURL(file),
    }));
    setPreparedPreviews(urls);
    return () => {
      urls.forEach((item) => URL.revokeObjectURL(item.url));
    };
  }, [formData.images]);

  useEffect(() => {
    if (!editImageFile) {
      setEditImagePreviewUrl("");
      return undefined;
    }

    const url = URL.createObjectURL(editImageFile);
    setEditImagePreviewUrl(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [editImageFile]);

  useEffect(() => {
    if (!isCropOpen) {
      setCropSourceUrl("");
      setCropImage(null);
      setIsPreviewDragging(false);
      setIsCropFrameDragging(false);
      setCropFrameDrag(null);
      return;
    }

    const file = cropQueue[cropIndex];
    const url = URL.createObjectURL(file);
    setCropSourceUrl(url);
    setCropImage(null);
    setCropError("");
    setCropX(50);
    setCropY(50);
    setCropZoom(1);
    setCropTrimLeft(0);
    setCropTrimRight(0);
    setCropTrimTop(0);
    setCropTrimBottom(0);
    setIsPreviewDragging(false);
    setIsCropFrameDragging(false);
    setCropFrameDrag(null);

    return () => URL.revokeObjectURL(url);
  }, [cropQueue, cropIndex, isCropOpen]);

  useEffect(() => {
    if (!cropSourceUrl) return;
    const image = new Image();
    image.onload = () => {
      setCropImage(image);
      setCropError("");
    };
    image.onerror = () => {
      setCropImage(null);
      setCropError("Could not load this image for cropping.");
    };
    image.src = cropSourceUrl;
    return () => {
      image.onload = null;
      image.onerror = null;
    };
  }, [cropSourceUrl]);

  useEffect(() => {
    if (!isCropOpen || !cropImage) return;
    const canvas = previewCanvasRef.current;
    if (!canvas) return;
    drawCropToCanvas(cropImage, canvas, {
      focusX: cropX / 100,
      focusY: cropY / 100,
      zoom: cropZoom,
      trimLeft: cropTrimLeft,
      trimRight: cropTrimRight,
      trimTop: cropTrimTop,
      trimBottom: cropTrimBottom,
    });
  }, [
    isCropOpen,
    cropImage,
    cropX,
    cropY,
    cropZoom,
    cropTrimLeft,
    cropTrimRight,
    cropTrimTop,
    cropTrimBottom,
  ]);

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const items = reorder(sliders, result.source.index, result.destination.index);
    setSliders(items);

    const payload = items.map((item, index) => ({
      id: item.id,
      position: index + 1,
    }));

    try {
      await API.put("/admin/sliders/reorder", { order: payload });
    } catch (err) {
      console.error("Slider reorder failed:", err);
      setMessage("Error: Failed to reorder sliders.");
    }
  };

  const handleTextChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const updateCropFocusFromPointer = (clientX, clientY) => {
    const canvas = previewCanvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const relativeX = clamp01((clientX - rect.left) / rect.width);
    const relativeY = clamp01((clientY - rect.top) / rect.height);
    setCropX(Math.round(relativeX * 100));
    setCropY(Math.round(relativeY * 100));
  };

  const startCropFrameDrag = (event, handle) => {
    if (!cropImage || !isCropOpen) return;
    const rect = cropFrameContainerRef.current?.getBoundingClientRect();
    if (!rect || !rect.width || !rect.height) return;

    event.preventDefault();
    event.stopPropagation();
    setIsPreviewDragging(false);
    setIsCropFrameDragging(true);
    setCropFrameDrag({
      handle,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      containerWidth: rect.width,
      containerHeight: rect.height,
      startLeft: cropTrimLeft,
      startRight: cropTrimRight,
      startTop: cropTrimTop,
      startBottom: cropTrimBottom,
    });
  };

  useEffect(() => {
    if (!cropFrameDrag) return;

    const clampRange = (value, min, max) => Math.min(max, Math.max(min, value));
    const onPointerMove = (event) => {
      if (event.pointerId !== cropFrameDrag.pointerId) return;

      const dxPct = ((event.clientX - cropFrameDrag.startX) / cropFrameDrag.containerWidth) * 100;
      const dyPct = ((event.clientY - cropFrameDrag.startY) / cropFrameDrag.containerHeight) * 100;

      let nextLeft = cropFrameDrag.startLeft;
      let nextRight = cropFrameDrag.startRight;
      let nextTop = cropFrameDrag.startTop;
      let nextBottom = cropFrameDrag.startBottom;

      if (cropFrameDrag.handle === "move") {
        const width = 100 - cropFrameDrag.startLeft - cropFrameDrag.startRight;
        const height = 100 - cropFrameDrag.startTop - cropFrameDrag.startBottom;
        const minLeft = Math.max(0, 100 - width - 45);
        const maxLeft = Math.min(45, 100 - width);
        const minTop = Math.max(0, 100 - height - 45);
        const maxTop = Math.min(45, 100 - height);

        nextLeft = clampRange(cropFrameDrag.startLeft + dxPct, minLeft, maxLeft);
        nextRight = 100 - width - nextLeft;
        nextTop = clampRange(cropFrameDrag.startTop + dyPct, minTop, maxTop);
        nextBottom = 100 - height - nextTop;
      } else {
        if (cropFrameDrag.handle.includes("left")) {
          nextLeft = clampRange(
            cropFrameDrag.startLeft + dxPct,
            0,
            Math.min(45, 90 - cropFrameDrag.startRight)
          );
        }
        if (cropFrameDrag.handle.includes("right")) {
          nextRight = clampRange(
            cropFrameDrag.startRight - dxPct,
            0,
            Math.min(45, 90 - cropFrameDrag.startLeft)
          );
        }
        if (cropFrameDrag.handle.includes("top")) {
          nextTop = clampRange(
            cropFrameDrag.startTop + dyPct,
            0,
            Math.min(45, 90 - cropFrameDrag.startBottom)
          );
        }
        if (cropFrameDrag.handle.includes("bottom")) {
          nextBottom = clampRange(
            cropFrameDrag.startBottom - dyPct,
            0,
            Math.min(45, 90 - cropFrameDrag.startTop)
          );
        }
      }

      setCropTrimLeft(Math.round(nextLeft));
      setCropTrimRight(Math.round(nextRight));
      setCropTrimTop(Math.round(nextTop));
      setCropTrimBottom(Math.round(nextBottom));
    };

    const onPointerEnd = (event) => {
      if (event.pointerId !== cropFrameDrag.pointerId) return;
      setCropFrameDrag(null);
      setIsCropFrameDragging(false);
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerEnd);
    window.addEventListener("pointercancel", onPointerEnd);

    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerEnd);
      window.removeEventListener("pointercancel", onPointerEnd);
    };
  }, [cropFrameDrag]);

  const handlePreviewPointerDown = (e) => {
    if (!cropImage) return;
    if (cropFrameDrag) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setIsPreviewDragging(true);
    updateCropFocusFromPointer(e.clientX, e.clientY);
  };

  const handlePreviewPointerMove = (e) => {
    if (!isPreviewDragging || !cropImage) return;
    updateCropFocusFromPointer(e.clientX, e.clientY);
  };

  const handlePreviewPointerUp = (e) => {
    if (isPreviewDragging) {
      updateCropFocusFromPointer(e.clientX, e.clientY);
      try {
        e.currentTarget.releasePointerCapture?.(e.pointerId);
      } catch (_err) {
        // Ignore if pointer capture is already released.
      }
    }
    setIsPreviewDragging(false);
  };

  const handlePreviewWheel = (event) => {
    event.preventDefault();
    const step = event.shiftKey ? 0.18 : 0.08;
    setCropZoom((prev) => {
      const next = prev + (event.deltaY < 0 ? step : -step);
      return Math.max(0.5, Math.min(3, next));
    });
  };

  const handleHorizontalTrimChange = (side, nextValue) => {
    const safeValue = Math.max(0, Math.min(45, Number(nextValue) || 0));
    if (side === "left") {
      const allowed = Math.max(0, 90 - cropTrimRight);
      setCropTrimLeft(Math.min(allowed, safeValue));
      return;
    }
    const allowed = Math.max(0, 90 - cropTrimLeft);
    setCropTrimRight(Math.min(allowed, safeValue));
  };

  const handleVerticalTrimChange = (side, nextValue) => {
    const safeValue = Math.max(0, Math.min(45, Number(nextValue) || 0));
    if (side === "top") {
      const allowed = Math.max(0, 90 - cropTrimBottom);
      setCropTrimTop(Math.min(allowed, safeValue));
      return;
    }
    const allowed = Math.max(0, 90 - cropTrimTop);
    setCropTrimBottom(Math.min(allowed, safeValue));
  };

  const clearCropEditorState = () => {
    setCropQueue([]);
    setCropIndex(-1);
    setCropSourceUrl("");
    setCropImage(null);
    setCropError("");
    setCropTrimLeft(0);
    setCropTrimRight(0);
    setCropTrimTop(0);
    setCropTrimBottom(0);
    setIsCropFrameDragging(false);
    setCropFrameDrag(null);
    setCropContext(CROP_CONTEXT_ADD);
  };

  const startCropSession = (files, context = CROP_CONTEXT_ADD) => {
    const queue = Array.from(files || []).filter(Boolean);
    if (!queue.length) return;
    setCropContext(context);
    setCropQueue(queue);
    setCropIndex(0);
    setCropError("");
  };

  const handleFitModeChange = (nextModeRaw) => {
    const nextMode = normalizeFitMode(nextModeRaw);
    const currentMode = normalizeFitMode(formData.fitMode);
    if (nextMode === currentMode) return;

    setFormData((prev) => ({ ...prev, fitMode: nextMode, images: [] }));
    clearCropEditorState();
    if (fileInputRef.current) fileInputRef.current.value = "";
    setMessage(`Info: Switched to ${fitModeLabel(nextMode)} mode. Please select images again.`);
  };

  const handleImagesSelected = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (!selectedFiles.length) {
      setFormData((prev) => ({ ...prev, images: [] }));
      clearCropEditorState();
      return;
    }
    const cappedFiles =
      selectedFiles.length > MAX_SLIDER_BATCH_UPLOAD
        ? selectedFiles.slice(0, MAX_SLIDER_BATCH_UPLOAD)
        : selectedFiles;
    const wasCapped = selectedFiles.length > MAX_SLIDER_BATCH_UPLOAD;
    const capNote = wasCapped
      ? ` Only first ${MAX_SLIDER_BATCH_UPLOAD} files are kept for this upload.`
      : "";

    const selectedFitMode = normalizeFitMode(formData.fitMode);
    if (selectedFitMode === FIT_MODE_CONTAIN || selectedFitMode === FIT_MODE_AUTO) {
      clearCropEditorState();
      setFormData((prev) => ({ ...prev, images: cappedFiles }));
      setMessage(
        `Success: ${cappedFiles.length} image(s) ready for upload in ${fitModeLabel(selectedFitMode)} mode.${capNote}`
      );
      return;
    }

    setFormData((prev) => ({ ...prev, images: [] }));
    startCropSession(cappedFiles, CROP_CONTEXT_ADD);
    setMessage(`Info: Crop and confirm each image, then submit.${capNote}`);
  };

  const advanceCropStep = (nextCount) => {
    if (cropIndex >= cropQueue.length - 1) {
      clearCropEditorState();
      setMessage(`Success: ${nextCount} image(s) ready for upload in Crop Fill mode.`);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setCropIndex((prev) => prev + 1);
  };

  const appendPreparedFile = (preparedFile) => {
    if (cropContext === CROP_CONTEXT_EDIT) {
      setEditImageFile(preparedFile);
      clearCropEditorState();
      if (editFileInputRef.current) editFileInputRef.current.value = "";
      setMessage("Success: Edited slider image is ready. Review preview and click Save Changes.");
      return;
    }

    const nextCount = (formData.images?.length || 0) + 1;
    setFormData((prev) => ({
      ...prev,
      images: [...(prev.images || []), preparedFile],
    }));
    advanceCropStep(nextCount);
  };

  const handleUseOriginal = async () => {
    if (!currentCropFile || !cropImage) return;
    setIsApplyingCrop(true);
    setCropError("");

    try {
      const canvas = document.createElement("canvas");
      canvas.width = CROPPED_WIDTH;
      canvas.height = CROPPED_HEIGHT;
      drawCropToCanvas(cropImage, canvas, {
        focusX: 0.5,
        focusY: 0.5,
        zoom: 1,
        trimLeft: 0,
        trimRight: 0,
        trimTop: 0,
        trimBottom: 0,
      });

      const mimeType = currentCropFile.type === "image/png" ? "image/png" : "image/jpeg";
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (result) => {
            if (result) resolve(result);
            else reject(new Error("Canvas export failed."));
          },
          mimeType,
          mimeType === "image/jpeg" ? 0.92 : undefined
        );
      });
      const normalizedFile = new File([blob], currentCropFile.name, {
        type: mimeType,
        lastModified: Date.now(),
      });
      appendPreparedFile(normalizedFile);
    } catch (err) {
      console.error("Use original failed:", err);
      setCropError("Failed to prepare original image. Try Save Crop instead.");
    } finally {
      setIsApplyingCrop(false);
    }
  };

  const handleApplyCrop = async () => {
    if (!currentCropFile || !cropImage) return;
    setIsApplyingCrop(true);
    setCropError("");

    try {
      const canvas = document.createElement("canvas");
      canvas.width = CROPPED_WIDTH;
      canvas.height = CROPPED_HEIGHT;
      drawCropToCanvas(cropImage, canvas, {
        focusX: cropX / 100,
        focusY: cropY / 100,
        zoom: cropZoom,
        trimLeft: cropTrimLeft,
        trimRight: cropTrimRight,
        trimTop: cropTrimTop,
        trimBottom: cropTrimBottom,
      });

      const mimeType = currentCropFile.type === "image/png" ? "image/png" : "image/jpeg";
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (result) => {
            if (result) resolve(result);
            else reject(new Error("Canvas export failed."));
          },
          mimeType,
          mimeType === "image/jpeg" ? 0.92 : undefined
        );
      });

      const croppedFile = new File([blob], currentCropFile.name, {
        type: mimeType,
        lastModified: Date.now(),
      });
      appendPreparedFile(croppedFile);
    } catch (err) {
      console.error("Slider crop failed:", err);
      setCropError("Failed to crop this image. Try again.");
    } finally {
      setIsApplyingCrop(false);
    }
  };

  const handleCancelCrop = () => {
    const context = cropContext;
    clearCropEditorState();
    if (context === CROP_CONTEXT_EDIT) {
      if (editFileInputRef.current) editFileInputRef.current.value = "";
      setMessage("Info: Edit image crop cancelled.");
      return;
    }

    setFormData((prev) => ({ ...prev, images: [] }));
    setMessage("Info: Image selection cleared.");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.images.length) {
      setMessage("Error: Please select images and complete image preparation.");
      return;
    }

    const fd = new FormData();
    fd.append("title", formData.title);
    fd.append("description", formData.description);
    fd.append("fit_mode", normalizeFitMode(formData.fitMode));
    formData.images.forEach((imageFile) => fd.append("images[]", imageFile));

    try {
      const res = await API.addSlider(fd);
      setMessage(`Success: ${res?.message || "Sliders added successfully."}`);
      setFormData((prev) => ({ title: "", description: "", images: [], fitMode: prev.fitMode }));
      if (fileInputRef.current) fileInputRef.current.value = "";
      await loadSliders();
    } catch (err) {
      console.error("Error adding sliders:", err);
      setMessage(`Error: ${err?.message || "Failed to add sliders."}`);
    }
  };

  const handleOpenEdit = (slider) => {
    if (!slider) return;

    clearCropEditorState();
    setEditingSlider(slider);
    setEditForm({
      title: slider.title || "",
      description: slider.description || "",
      fitMode: normalizeFitMode(slider.fit_mode),
    });
    setEditImageFile(null);
    setEditImagePreviewUrl("");
    setIsPreparingEditImage(false);
    if (editFileInputRef.current) editFileInputRef.current.value = "";
  };

  const handleCloseEdit = () => {
    clearCropEditorState();
    setEditingSlider(null);
    setEditForm({
      title: "",
      description: "",
      fitMode: FIT_MODE_COVER,
    });
    setEditImageFile(null);
    setEditImagePreviewUrl("");
    setIsPreparingEditImage(false);
    if (editFileInputRef.current) editFileInputRef.current.value = "";
  };

  const handleEditTextChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditImageSelected = (e) => {
    const file = (e.target.files || [])[0] || null;
    if (!file) {
      setEditImageFile(null);
      return;
    }

    if (normalizeFitMode(editForm.fitMode) === FIT_MODE_COVER) {
      startCropSession([file], CROP_CONTEXT_EDIT);
      setMessage("Info: Adjust crop for replacement slider image, then save changes.");
      return;
    }

    setEditImageFile(file);
    setMessage(`Success: Replacement image selected in ${fitModeLabel(editForm.fitMode)} mode.`);
  };

  const handleEditAdjustCurrentImage = async () => {
    if (!editingSlider?.image) {
      setMessage("Error: No current slider image found to adjust.");
      return;
    }

    try {
      setIsPreparingEditImage(true);
      const sourceUrl = resolveAssetUrl(editingSlider.image, { folder: "sliders" });
      const response = await fetch(sourceUrl, { cache: "no-store" });
      if (!response.ok) {
        throw new Error(`Failed to fetch current image (${response.status}).`);
      }
      const blob = await response.blob();
      const mimeType = blob.type === "image/png" ? "image/png" : "image/jpeg";
      const extension = mimeType === "image/png" ? "png" : "jpg";
      const cleanBase = String(editingSlider.title || `slider-${editingSlider.id || "image"}`)
        .trim()
        .replace(/[^a-z0-9_-]+/gi, "_")
        .replace(/^_+|_+$/g, "");
      const fileBase = cleanBase || `slider-${editingSlider.id || "image"}`;
      const preparedFile = new File([blob], `${fileBase}-adjust.${extension}`, {
        type: mimeType,
        lastModified: Date.now(),
      });

      startCropSession([preparedFile], CROP_CONTEXT_EDIT);
      setMessage("Info: Adjust current slider image crop, then save changes.");
    } catch (err) {
      console.error("Error preparing current slider image for crop:", err);
      setMessage("Error: Failed to open current image for cropping.");
    } finally {
      setIsPreparingEditImage(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingSlider?.id) return;

    const title = String(editForm.title || "").trim();
    if (!title) {
      setMessage("Error: Slider title is required.");
      return;
    }

    const fd = new FormData();
    fd.append("_method", "PUT");
    fd.append("title", title);
    fd.append("description", String(editForm.description || ""));
    fd.append("fit_mode", normalizeFitMode(editForm.fitMode));
    if (editImageFile) {
      fd.append("image", editImageFile);
    }

    try {
      setIsSavingEdit(true);
      const res = await API.updateSlider(editingSlider.id, fd);
      setMessage(`Success: ${res?.message || "Slider updated successfully."}`);
      handleCloseEdit();
      await loadSliders();
    } catch (err) {
      console.error("Error updating slider:", err);
      setMessage(`Error: ${err?.message || "Failed to update slider."}`);
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleToggle = async (id) => {
    try {
      const res = await API.toggleSlider(id);
      setMessage(`Success: Slider ${String(res?.newStatus || "").toLowerCase()} successfully.`);
      await loadSliders();
    } catch {
      setMessage("Error: Failed to update slider status.");
    }
  };

  const handleDelete = async (id) => {
    const result = await confirmAlert({
      title: "Delete this slider?",
      text: "This action cannot be undone",
      confirmText: "Delete",
    });
    if (!result.isConfirmed) return;
    try {
      await API.deleteSlider(id);
      setMessage("Success: Slider deleted successfully.");
      await loadSliders();
    } catch {
      setMessage("Error: Failed to delete slider.");
    }
  };

  if (!["admin", "secretary", "district"].includes(String(role || "").toLowerCase())) {
    return (
      <div className="flex justify-center items-center h-screen text-red-500">
        Unauthorized Access
      </div>
    );
  }

  const messageStyle = message.startsWith("Error:")
    ? "bg-red-100 text-red-700"
    : message.startsWith("Success:")
      ? "bg-green-100 text-green-700"
      : "bg-blue-100 text-blue-700";
  const sliderPreviewCount = preparedPreviews.length;
  const sliderPreviewImage = sliderPreviewCount ? preparedPreviews[0].url : "";
  const sliderPreviewTitle = String(formData.title || "").trim();
  const sliderPreviewFitMode = normalizeFitMode(formData.fitMode);
  const sliderDisplayHint = sliderPreviewCount
    ? sliderPreviewCount === 1
      ? "Homepage slider shows 1 slide at a time (single image uploaded)."
      : `Homepage slider shows 1 slide at a time. ${sliderPreviewCount} slides will rotate with navigation dots.`
    : "Select images to preview slider display and rotation count.";
  const sliderModeAreaHint =
    sliderPreviewFitMode === FIT_MODE_CONTAIN
      ? "Display uses full source image (no crop). Extra area uses a subtle image backdrop."
      : sliderPreviewFitMode === FIT_MODE_AUTO
        ? "Full image is preserved on all devices with a subtle backdrop if needed."
        : "No automatic crop. Full image is preserved. Use crop controls (zoom > 100%) for manual crop.";
  const editDisplayImage = editImagePreviewUrl || editCurrentImageUrl;
  const editPreviewTitle = String(editForm.title || editingSlider?.title || "").trim();
  const editPreviewFitMode = normalizeFitMode(editForm.fitMode);
  const editPreviewHint = editImagePreviewUrl
    ? "Previewing replacement image."
    : "Previewing current slider image.";
  const isEditCropSession = cropContext === CROP_CONTEXT_EDIT;
  const cropVisibleWidthPct = Math.max(1, Math.round(100 - cropTrimLeft - cropTrimRight));
  const cropVisibleHeightPct = Math.max(1, Math.round(100 - cropTrimTop - cropTrimBottom));
  const cropVisibleAreaPct = Math.max(
    1,
    Math.round((cropVisibleWidthPct * cropVisibleHeightPct) / 100)
  );
  const cropFrameStyle = {
    left: `${cropTrimLeft}%`,
    top: `${cropTrimTop}%`,
    width: `${cropVisibleWidthPct}%`,
    height: `${cropVisibleHeightPct}%`,
  };

  return (
    <>
      <Navbar />
      <div className="max-w-6xl mx-auto p-3 sm:p-4 md:p-6 mobile-page-pad">
        <section className="mb-6 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-cyan-50 p-4 sm:p-5 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-cyan-700">Media Studio</p>
              <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 mt-1">Manage Sliders</h1>
              <p className="text-sm text-slate-600 mt-1">
                Prepare hero images, preview fit modes, and publish/reorder slider records.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Sliders</p>
                <p className="text-lg font-semibold text-slate-900">{sliders.length}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Prepared</p>
                <p className="text-lg font-semibold text-slate-900">{preparedPreviews.length}</p>
              </div>
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-emerald-700">Mode</p>
                <p className="text-lg font-semibold text-emerald-800">{fitModeLabel(formData.fitMode)}</p>
              </div>
              <div className="rounded-xl border border-cyan-200 bg-cyan-50 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-cyan-700">Status</p>
                <p className="text-lg font-semibold text-cyan-800">{message ? "Updated" : "Ready"}</p>
              </div>
            </div>
          </div>
        </section>

        {message && (
          <div className={`text-center p-2 mb-4 rounded ${messageStyle}`}>
            {message}
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-md mb-8">
          <div className="grid md:grid-cols-2 gap-4">
            <input
              type="text"
              name="title"
              placeholder="Slider Title"
              value={formData.title}
              onChange={handleTextChange}
              className="border p-2 rounded"
              required
            />
            <input
              ref={fileInputRef}
              type="file"
              name="images"
              accept="image/*"
              multiple
              onChange={handleImagesSelected}
              className="border p-2 rounded"
            />
          </div>

          <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <p className="text-sm font-semibold text-slate-700">Image processing mode</p>
            <div className="mt-2 grid gap-2 sm:grid-cols-3">
              <label className="flex items-start gap-2 rounded border border-slate-200 bg-white p-2 text-sm text-slate-700">
                <input
                  type="radio"
                  name="fitMode"
                  value={FIT_MODE_COVER}
                  checked={normalizeFitMode(formData.fitMode) === FIT_MODE_COVER}
                  onChange={(e) => handleFitModeChange(e.target.value)}
                  disabled={isCropOpen}
                  className="mt-1"
                />
                <span>
                  <strong>Crop Fill (16:9)</strong>
                  <br />
                  No automatic crop. Full image stays visible. Use zoom above 100% for manual crop.
                </span>
              </label>
              <label className="flex items-start gap-2 rounded border border-slate-200 bg-white p-2 text-sm text-slate-700">
                <input
                  type="radio"
                  name="fitMode"
                  value={FIT_MODE_CONTAIN}
                  checked={normalizeFitMode(formData.fitMode) === FIT_MODE_CONTAIN}
                  onChange={(e) => handleFitModeChange(e.target.value)}
                  disabled={isCropOpen}
                  className="mt-1"
                />
                <span>
                  <strong>Fit to Page</strong>
                  <br />
                  Never crops images, including mobile. Extra area uses a subtle image backdrop.
                </span>
              </label>
              <label className="flex items-start gap-2 rounded border border-slate-200 bg-white p-2 text-sm text-slate-700">
                <input
                  type="radio"
                  name="fitMode"
                  value={FIT_MODE_AUTO}
                  checked={normalizeFitMode(formData.fitMode) === FIT_MODE_AUTO}
                  onChange={(e) => handleFitModeChange(e.target.value)}
                  disabled={isCropOpen}
                  className="mt-1"
                />
                <span>
                  <strong>Auto</strong>
                  <br />
                  Preserves full image on all devices with a subtle backdrop if needed.
                </span>
              </label>
            </div>
          </div>

          <p className="text-xs text-slate-500 mt-2">
            {normalizeFitMode(formData.fitMode) === FIT_MODE_COVER
              ? "Selecting images opens a 16:9 crop preview. Confirm each image, then click Add Slider."
              : normalizeFitMode(formData.fitMode) === FIT_MODE_CONTAIN
                ? "Fit to Page mode uses original image dimensions (no crop)."
                : "Auto mode uses original image dimensions (no crop) on all devices."
          }
          </p>

          {preparedPreviews.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-semibold text-slate-700">
                {preparedPreviews.length} image(s) ready for upload
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                {preparedPreviews.map((item, idx) => (
                  <div key={`${item.name}-${idx}`} className="rounded border border-slate-200 overflow-hidden">
                    <img
                      src={item.url}
                      alt={item.name}
                      className={`w-full h-24 ${sliderThumbImageClass(formData.fitMode)}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4 rounded border border-slate-200 bg-slate-50 p-3">
            <p className="text-sm font-semibold text-slate-800">Homepage slider preview</p>
            <p className="text-xs text-slate-600 mt-1">
              Mode: <strong>{fitModeLabel(formData.fitMode)}</strong>. {sliderDisplayHint}
            </p>
            <p className="text-xs text-slate-600 mt-1">{sliderModeAreaHint}</p>
            {!sliderPreviewImage ? (
              <div className="mt-3 rounded border border-dashed border-slate-300 bg-white p-4 text-sm text-slate-500">
                No prepared images yet.
              </div>
            ) : (
              <div className="mt-3 rounded border border-slate-200 bg-white p-3">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  <div className="rounded border border-slate-200 p-2 lg:col-span-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                      Desktop preview
                    </p>
                    <div
                      className={`relative w-full h-[280px] sm:h-[340px] md:h-[420px] lg:h-[500px] xl:h-[580px] rounded overflow-hidden ${sliderPreviewFrameClass(
                        sliderPreviewFitMode
                      )}`}
                    >
                      <img
                        src={sliderPreviewImage}
                        alt=""
                        aria-hidden="true"
                        className="absolute inset-0 w-full h-full object-cover opacity-10 blur-[4px] scale-110"
                      />
                      <img
                        src={sliderPreviewImage}
                        alt="Desktop slider preview"
                        className={`relative z-10 w-full h-full ${sliderPreviewImageClass(sliderPreviewFitMode)}`}
                      />
                      {sliderPreviewTitle ? (
                        <div className="absolute z-20 left-3 top-3 max-w-[80%] rounded-lg border border-white/30 bg-black/40 px-3 py-2 backdrop-blur-sm">
                          <p className="text-left text-sm sm:text-base md:text-lg font-extrabold text-white leading-snug line-clamp-2">
                            {sliderPreviewTitle}
                          </p>
                        </div>
                      ) : null}
                      <div className="absolute z-20 right-2 bottom-2 rounded bg-black/65 px-2 py-1 text-xs text-white">
                        1/{sliderPreviewCount}
                      </div>
                    </div>
                  </div>
                  <div className="rounded border border-slate-200 p-2 lg:max-w-[360px]">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                      Mobile preview
                    </p>
                    <div
                      className={`relative w-full max-w-[320px] h-[280px] rounded overflow-hidden mx-auto ${sliderPreviewFrameClass(
                        sliderPreviewFitMode
                      )}`}
                    >
                      <img
                        src={sliderPreviewImage}
                        alt=""
                        aria-hidden="true"
                        className="absolute inset-0 w-full h-full object-cover opacity-10 blur-[4px] scale-110"
                      />
                      <img
                        src={sliderPreviewImage}
                        alt="Mobile slider preview"
                        className={`relative z-10 w-full h-full ${sliderPreviewImageClass(sliderPreviewFitMode)}`}
                      />
                      {sliderPreviewTitle ? (
                        <div className="absolute z-20 left-2 top-2 max-w-[88%] rounded-lg border border-white/30 bg-black/40 px-2.5 py-1.5 backdrop-blur-sm">
                          <p className="text-left text-xs sm:text-sm font-bold text-white leading-snug line-clamp-2">
                            {sliderPreviewTitle}
                          </p>
                        </div>
                      ) : null}
                      <div className="absolute z-20 right-2 bottom-2 rounded bg-black/65 px-2 py-1 text-xs text-white">
                        1/{sliderPreviewCount}
                      </div>
                    </div>
                  </div>
                </div>
                {sliderPreviewCount > 1 ? (
                  <div className="mt-2 flex items-center justify-center gap-1.5">
                    {preparedPreviews.slice(0, 8).map((item, idx) => (
                      <span
                        key={`${item.name}-${idx}`}
                        className={`h-2 rounded-full ${
                          idx === 0 ? "w-6 bg-blue-600" : "w-2 bg-slate-300"
                        }`}
                      />
                    ))}
                    {sliderPreviewCount > 8 ? (
                      <span className="text-xs text-slate-500">+{sliderPreviewCount - 8}</span>
                    ) : null}
                  </div>
                ) : null}
              </div>
            )}
          </div>

          <textarea
            name="description"
            placeholder="Description (optional)"
            value={formData.description}
            onChange={handleTextChange}
            rows="3"
            className="border p-2 rounded w-full mt-3"
          />
          <div className="flex justify-end mt-4">
            <button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
            >
              Add Slider
            </button>
          </div>
        </form>

        <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="border-b border-slate-200 bg-slate-50/70 px-4 py-3">
            <h2 className="text-lg font-semibold text-slate-900">Slider Records</h2>
            <p className="text-sm text-slate-600">Drag to reorder, edit slider text/media, activate/deactivate records.</p>
          </div>
        <div className="overflow-x-auto">
          <table className="table-fixed min-w-full border border-gray-300 rounded-lg">
            <thead className="bg-blue-100 text-gray-700">
              <tr>
                <th className="p-3 border">Image</th>
                <th className="p-3 border">Title</th>
                <th className="p-3 border">Description</th>
                <th className="p-3 border">Status</th>
                <th className="p-3 border">Actions</th>
              </tr>
            </thead>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="sliders">
                {(provided) => (
                  <tbody ref={provided.innerRef} {...provided.droppableProps}>
                    {sliders.length ? (
                      sliders.map((slider, index) => (
                        <Draggable
                          key={slider.id}
                          draggableId={String(slider.id)}
                          index={index}
                        >
                          {(dragProvided) => (
                            <tr
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              {...dragProvided.dragHandleProps}
                              style={{
                                ...dragProvided.draggableProps.style,
                                display: "table-row",
                              }}
                              className="text-center hover:bg-gray-50 cursor-move"
                            >
                              <td className="p-3 border">
                                <img
                                  src={resolveAssetUrl(slider.image, { folder: "sliders" })}
                                  alt={slider.title}
                                  className={`w-32 h-16 mx-auto rounded ${sliderThumbImageClass(
                                    slider.fit_mode || slider.fitMode
                                  )}`}
                                />
                              </td>
                              <td className="p-3 border">{slider.title}</td>
                              <td className="p-3 border text-sm text-gray-600">
                                {slider.description || "-"}
                              </td>
                              <td className="p-3 border">
                                <span
                                  className={`px-3 py-1 rounded-full text-sm font-semibold ${
                                    slider.status === "Active"
                                      ? "bg-green-100 text-green-700"
                                      : "bg-red-100 text-red-600"
                                  }`}
                                >
                                  {slider.status}
                                </span>
                              </td>
                              <td className="p-3 border space-x-2">
                                <button
                                  onClick={() => handleOpenEdit(slider)}
                                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1 rounded"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleToggle(slider.id)}
                                  className={`px-3 py-1 rounded text-white ${
                                    slider.status === "Active"
                                      ? "bg-gray-500 hover:bg-gray-600"
                                      : "bg-green-600 hover:bg-green-700"
                                  }`}
                                >
                                  {slider.status === "Active" ? "Deactivate" : "Activate"}
                                </button>
                                <button
                                  onClick={() => handleDelete(slider.id)}
                                  className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded"
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          )}
                        </Draggable>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="p-3 text-center text-gray-500">
                          No sliders found.
                        </td>
                      </tr>
                    )}
                    {provided.placeholder}
                  </tbody>
                )}
              </Droppable>
            </DragDropContext>
          </table>
        </div>
        </section>
      </div>

      {editingSlider && (
        <div className="fixed inset-0 z-50 bg-black/70 p-2 sm:p-4 flex items-start sm:items-center justify-center overflow-y-auto">
          <div className="w-full max-w-3xl max-h-[calc(100vh-1rem)] sm:max-h-[calc(100vh-2rem)] overflow-y-auto bg-white rounded-xl shadow-xl p-4 sm:p-5">
            <h3 className="text-xl font-bold text-slate-900">Edit Slider</h3>
            <p className="text-sm text-slate-600 mt-1">
              Update title, description, fit mode, or replace image.
            </p>

            <form onSubmit={handleEditSubmit} className="mt-4 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <label className="text-sm text-slate-700">
                  Title
                  <input
                    type="text"
                    name="title"
                    value={editForm.title}
                    onChange={handleEditTextChange}
                    className="border p-2 rounded w-full mt-1"
                    required
                  />
                </label>
                <label className="text-sm text-slate-700">
                  Fit mode
                  <select
                    name="fitMode"
                    value={normalizeFitMode(editForm.fitMode)}
                    onChange={handleEditTextChange}
                    className="border p-2 rounded w-full mt-1"
                  >
                    <option value={FIT_MODE_COVER}>Crop Fill</option>
                    <option value={FIT_MODE_CONTAIN}>Fit to Page</option>
                    <option value={FIT_MODE_AUTO}>Auto</option>
                  </select>
                </label>
              </div>

              <label className="text-sm text-slate-700 block">
                Description
                <textarea
                  name="description"
                  rows="3"
                  value={editForm.description}
                  onChange={handleEditTextChange}
                  className="border p-2 rounded w-full mt-1"
                />
              </label>

              <label className="text-sm text-slate-700 block">
                Replace image (optional)
                <input
                  ref={editFileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleEditImageSelected}
                  className="border p-2 rounded w-full mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">
                  In Crop Fill mode, selecting an image opens crop + preview before saving.
                </p>
              </label>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <p className="text-xs font-semibold text-slate-500">Current image</p>
                    <button
                      type="button"
                      onClick={handleEditAdjustCurrentImage}
                      disabled={isPreparingEditImage || isCropOpen}
                      className="text-xs px-2 py-1 rounded border border-slate-300 text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                    >
                      {isPreparingEditImage ? "Preparing..." : "Adjust Current"}
                    </button>
                  </div>
                  <div className="relative w-full h-40 rounded border border-slate-200 overflow-hidden bg-slate-900">
                    <img
                      src={resolveAssetUrl(editingSlider.image, { folder: "sliders" })}
                      alt=""
                      aria-hidden="true"
                      className="absolute inset-0 w-full h-full object-cover opacity-10 blur-[4px] scale-110"
                    />
                    <img
                      src={resolveAssetUrl(editingSlider.image, { folder: "sliders" })}
                      alt={editingSlider.title || "Current slider"}
                      className={`relative z-10 w-full h-full ${sliderPreviewImageClass(editForm.fitMode)}`}
                    />
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-500 mb-1">New image preview</p>
                  {editImagePreviewUrl ? (
                    <div className="relative w-full h-40 rounded border border-slate-200 overflow-hidden bg-slate-900">
                      <img
                        src={editImagePreviewUrl}
                        alt=""
                        aria-hidden="true"
                        className="absolute inset-0 w-full h-full object-cover opacity-10 blur-[4px] scale-110"
                      />
                      <img
                        src={editImagePreviewUrl}
                        alt="New slider preview"
                        className={`relative z-10 w-full h-full ${sliderPreviewImageClass(editForm.fitMode)}`}
                      />
                    </div>
                  ) : (
                    <div className="w-full h-40 rounded border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center text-sm text-slate-500">
                      No replacement selected
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded border border-slate-200 bg-slate-50 p-3">
                <p className="text-sm font-semibold text-slate-800">Homepage display preview (edit)</p>
                <p className="text-xs text-slate-600 mt-1">
                  Mode: <strong>{fitModeLabel(editForm.fitMode)}</strong>. {editPreviewHint}
                </p>
                <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
                  <div className="rounded border border-slate-200 p-2 bg-white lg:col-span-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                      Desktop preview
                    </p>
                    <div
                      className={`relative w-full h-[280px] sm:h-[340px] md:h-[420px] lg:h-[500px] xl:h-[580px] rounded overflow-hidden ${sliderPreviewFrameClass(
                        editPreviewFitMode
                      )}`}
                    >
                      {editDisplayImage ? (
                        <>
                          <img
                            src={editDisplayImage}
                            alt=""
                            aria-hidden="true"
                            className="absolute inset-0 w-full h-full object-cover opacity-10 blur-[4px] scale-110"
                          />
                          <img
                            src={editDisplayImage}
                            alt="Edit desktop preview"
                            className={`relative z-10 w-full h-full ${sliderPreviewImageClass(editPreviewFitMode)}`}
                          />
                          {editPreviewTitle ? (
                            <div className="absolute z-20 left-3 top-3 max-w-[80%] rounded-lg border border-white/30 bg-black/40 px-3 py-2 backdrop-blur-sm">
                              <p className="text-left text-sm sm:text-base md:text-lg font-extrabold text-white leading-snug line-clamp-2">
                                {editPreviewTitle}
                              </p>
                            </div>
                          ) : null}
                        </>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-xs text-slate-500 bg-slate-100">
                          No image available
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="rounded border border-slate-200 p-2 bg-white lg:max-w-[360px]">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                      Mobile preview
                    </p>
                    <div
                      className={`relative w-full max-w-[320px] h-[280px] rounded overflow-hidden mx-auto ${sliderPreviewFrameClass(
                        editPreviewFitMode
                      )}`}
                    >
                      {editDisplayImage ? (
                        <>
                          <img
                            src={editDisplayImage}
                            alt=""
                            aria-hidden="true"
                            className="absolute inset-0 w-full h-full object-cover opacity-10 blur-[4px] scale-110"
                          />
                          <img
                            src={editDisplayImage}
                            alt="Edit mobile preview"
                            className={`relative z-10 w-full h-full ${sliderPreviewImageClass(editPreviewFitMode)}`}
                          />
                          {editPreviewTitle ? (
                            <div className="absolute z-20 left-2 top-2 max-w-[88%] rounded-lg border border-white/30 bg-black/40 px-2.5 py-1.5 backdrop-blur-sm">
                              <p className="text-left text-xs sm:text-sm font-bold text-white leading-snug line-clamp-2">
                                {editPreviewTitle}
                              </p>
                            </div>
                          ) : null}
                        </>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-xs text-slate-500 bg-slate-100">
                          No image available
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="sticky bottom-0 z-10 flex justify-end gap-2 bg-white pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={handleCloseEdit}
                  disabled={isSavingEdit}
                  className="px-4 py-2 rounded border border-slate-300 text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSavingEdit}
                  className="px-4 py-2 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  {isSavingEdit ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isCropOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 p-2 sm:p-4 flex items-start sm:items-center justify-center overflow-y-auto">
          <div className="w-full max-w-3xl max-h-[calc(100vh-1rem)] sm:max-h-[calc(100vh-2rem)] overflow-y-auto bg-white rounded-xl shadow-xl p-4 sm:p-5">
            <h3 className="text-xl font-bold text-slate-900">
              {isEditCropSession ? "Crop Slider Image (Edit)" : "Crop Slider Image"} ({cropIndex + 1}/
              {cropQueue.length})
            </h3>
            <p className="text-sm text-slate-600 mt-1">
              {currentCropFile?.name || "Selected image"} - Crop Fill output ratio 16:9
            </p>

            <div className="mt-4 p-3 border rounded-lg bg-slate-50 flex items-center justify-center">
              <div
                ref={cropFrameContainerRef}
                className={`relative w-full max-w-[640px] rounded border border-slate-200 bg-slate-200 overflow-hidden touch-none ${
                  cropImage
                    ? isPreviewDragging || isCropFrameDragging
                      ? "cursor-grabbing"
                      : "cursor-grab"
                    : "cursor-not-allowed"
                }`}
                onWheel={handlePreviewWheel}
                onPointerDown={handlePreviewPointerDown}
                onPointerMove={handlePreviewPointerMove}
                onPointerUp={handlePreviewPointerUp}
                onPointerCancel={handlePreviewPointerUp}
                onPointerLeave={handlePreviewPointerUp}
              >
                <canvas
                  ref={previewCanvasRef}
                  width={PREVIEW_WIDTH}
                  height={PREVIEW_HEIGHT}
                  className="w-full h-auto block select-none"
                />
                {cropImage ? (
                  <div className="pointer-events-none absolute inset-0">
                    <div
                      className="pointer-events-auto absolute border-2 border-blue-500"
                      style={{
                        ...cropFrameStyle,
                        boxShadow: "0 0 0 9999px rgba(15, 23, 42, 0.35)",
                      }}
                    >
                      <div
                        className="absolute -left-2 top-1/2 -translate-y-1/2 h-4 w-4 rounded-full border-2 border-blue-600 bg-white cursor-ew-resize"
                        onPointerDown={(event) => startCropFrameDrag(event, "left")}
                      />
                      <div
                        className="absolute -right-2 top-1/2 -translate-y-1/2 h-4 w-4 rounded-full border-2 border-blue-600 bg-white cursor-ew-resize"
                        onPointerDown={(event) => startCropFrameDrag(event, "right")}
                      />
                      <div
                        className="absolute left-1/2 -top-2 -translate-x-1/2 h-4 w-4 rounded-full border-2 border-blue-600 bg-white cursor-ns-resize"
                        onPointerDown={(event) => startCropFrameDrag(event, "top")}
                      />
                      <div
                        className="absolute left-1/2 -bottom-2 -translate-x-1/2 h-4 w-4 rounded-full border-2 border-blue-600 bg-white cursor-ns-resize"
                        onPointerDown={(event) => startCropFrameDrag(event, "bottom")}
                      />
                      <div
                        className="absolute -left-2 -top-2 h-4 w-4 rounded-full border-2 border-blue-700 bg-white cursor-nwse-resize"
                        onPointerDown={(event) => startCropFrameDrag(event, "top-left")}
                      />
                      <div
                        className="absolute -right-2 -top-2 h-4 w-4 rounded-full border-2 border-blue-700 bg-white cursor-nesw-resize"
                        onPointerDown={(event) => startCropFrameDrag(event, "top-right")}
                      />
                      <div
                        className="absolute -left-2 -bottom-2 h-4 w-4 rounded-full border-2 border-blue-700 bg-white cursor-nesw-resize"
                        onPointerDown={(event) => startCropFrameDrag(event, "bottom-left")}
                      />
                      <div
                        className="absolute -right-2 -bottom-2 h-4 w-4 rounded-full border-2 border-blue-700 bg-white cursor-nwse-resize"
                        onPointerDown={(event) => startCropFrameDrag(event, "bottom-right")}
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Tip: drag image to set focus. Drag crop frame edges/corners to trim. Scroll to zoom in/out.
            </p>
            <p className="text-xs text-slate-500 mt-1">
              Visible source after side-crop: width <strong>{cropVisibleWidthPct}%</strong>, height{" "}
              <strong>{cropVisibleHeightPct}%</strong>, area <strong>{cropVisibleAreaPct}%</strong>.
            </p>

            {cropError && (
              <p className="text-sm text-red-600 mt-3">{cropError}</p>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <label className="text-sm text-slate-700">
                Horizontal Focus
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={cropX}
                  onChange={(e) => setCropX(Number(e.target.value))}
                  className="w-full"
                />
              </label>
              <label className="text-sm text-slate-700">
                Vertical Focus
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={cropY}
                  onChange={(e) => setCropY(Number(e.target.value))}
                  className="w-full"
                />
              </label>
              <label className="text-sm text-slate-700">
                Zoom
                <input
                  type="range"
                  min="0.5"
                  max="3"
                  step="0.01"
                  value={cropZoom}
                  onChange={(e) => setCropZoom(Number(e.target.value))}
                  className="w-full"
                />
                <div className="text-xs text-slate-500 mt-1">{Math.round(cropZoom * 100)}%</div>
              </label>
              <label className="text-sm text-slate-700">
                Crop Left
                <input
                  type="range"
                  min="0"
                  max="45"
                  step="1"
                  value={cropTrimLeft}
                  onChange={(e) => handleHorizontalTrimChange("left", e.target.value)}
                  className="w-full"
                />
                <div className="text-xs text-slate-500 mt-1">{cropTrimLeft}%</div>
              </label>
              <label className="text-sm text-slate-700">
                Crop Right
                <input
                  type="range"
                  min="0"
                  max="45"
                  step="1"
                  value={cropTrimRight}
                  onChange={(e) => handleHorizontalTrimChange("right", e.target.value)}
                  className="w-full"
                />
                <div className="text-xs text-slate-500 mt-1">{cropTrimRight}%</div>
              </label>
              <label className="text-sm text-slate-700">
                Crop Top
                <input
                  type="range"
                  min="0"
                  max="45"
                  step="1"
                  value={cropTrimTop}
                  onChange={(e) => handleVerticalTrimChange("top", e.target.value)}
                  className="w-full"
                />
                <div className="text-xs text-slate-500 mt-1">{cropTrimTop}%</div>
              </label>
              <label className="text-sm text-slate-700">
                Crop Bottom
                <input
                  type="range"
                  min="0"
                  max="45"
                  step="1"
                  value={cropTrimBottom}
                  onChange={(e) => handleVerticalTrimChange("bottom", e.target.value)}
                  className="w-full"
                />
                <div className="text-xs text-slate-500 mt-1">{cropTrimBottom}%</div>
              </label>
            </div>

            <div className="sticky bottom-0 z-10 flex flex-wrap justify-between items-center gap-3 mt-5 bg-white pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={handleCancelCrop}
                className="px-4 py-2 rounded border border-slate-300 text-slate-700 hover:bg-slate-100"
              >
                Cancel
              </button>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleUseOriginal}
                  disabled={isApplyingCrop}
                  className="px-4 py-2 rounded border border-slate-300 text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                >
                  Use Original
                </button>
                <button
                  type="button"
                  onClick={handleApplyCrop}
                  disabled={!cropImage || isApplyingCrop}
                  className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {isApplyingCrop
                    ? "Applying..."
                    : cropIndex === cropQueue.length - 1
                      ? "Save Crop"
                      : "Save Crop & Next"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ManageSliders;
